import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Flag, CheckCircle2 } from "lucide-react";
import { api, apiError, fmtDate } from "../lib/api";
import { EmptyState } from "../components/EmptyState";

export default function ReviewFlagsPage() {
    const [flags, setFlags] = useState([]);
    const [status, setStatus] = useState("open");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [sites, setSites] = useState([]);

    async function refresh() {
        setLoading(true);
        try {
            const [f, s] = await Promise.all([
                api.get("/review-flags", { params: status ? { status } : {} }),
                api.get("/sites"),
            ]);
            setFlags(f.data || []);
            setSites(s.data || []);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [status]);

    const siteName = (id) => (sites.find((x) => x.id === id) || {}).name || id || "—";

    async function resolve(fid) {
        setBusy(fid);
        try {
            await api.post(`/review-flags/${fid}/resolve`);
            toast.success("Marked resolved");
            refresh();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(null); }
    }

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-8" data-testid="review-flags-page">
            <div>
                <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Import review queue</div>
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1 flex items-center gap-2">
                    <Flag className="w-6 h-6 text-[#E02B2B]" /> Flagged for review
                </h1>
                <p className="text-sm text-gray-500 mt-1">Historical import items that need Owner/Head confirmation before the month is finalised.</p>
            </div>

            <div className="mt-4 flex gap-2 text-xs">
                {["open", "resolved", ""].map((s) => (
                    <button
                        key={s || "all"}
                        onClick={() => setStatus(s)}
                        className={`px-3 py-1.5 rounded-md border ${status === s ? "bg-[#1E3355] text-white border-[#1E3355]" : "bg-white text-[#1E3355] border-gray-200 hover:bg-gray-50"}`}
                        data-testid={`flag-tab-${s || "all"}`}
                    >
                        {s ? s.charAt(0).toUpperCase() + s.slice(1) : "All"}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="mt-6 h-40 rounded-xl bg-white border border-gray-100 animate-pulse" />
            ) : flags.length === 0 ? (
                <EmptyState
                    icon="🎉"
                    title="All quiet — nothing to review"
                    subtitle={status === "open" ? "No import lines currently need your confirmation." : "No flags in this view."}
                    testid="no-flags"
                />
            ) : (
                <div className="mt-6 space-y-3">
                    {flags.map((f) => (
                        <div key={f.id} className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4" data-testid={`flag-${f.kind}`}>
                            <div className="flex justify-between flex-wrap gap-2">
                                <div className="flex-1 min-w-[15rem]">
                                    <div className="text-sm font-semibold text-[#1E3355]">
                                        {siteName(f.site_id)} {f.month ? `· ${f.month}` : ""}
                                    </div>
                                    <div className="text-xs uppercase tracking-widest text-gray-500 mt-0.5">{f.kind}</div>
                                    <div className="text-sm mt-2 text-gray-700 whitespace-pre-wrap">{f.reason}</div>
                                    {f.payload && Object.keys(f.payload).length > 0 && (
                                        <details className="mt-2 text-xs text-gray-500">
                                            <summary className="cursor-pointer">Payload</summary>
                                            <pre className="mt-1 bg-[#F4F6F9] rounded p-2 overflow-auto">{JSON.stringify(f.payload, null, 2)}</pre>
                                        </details>
                                    )}
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold border uppercase tracking-wider ${f.status === "open" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-emerald-50 text-emerald-800 border-emerald-200"}`}>
                                        {f.status}
                                    </span>
                                    <span className="text-[10px] text-gray-400">{fmtDate(f.created_at)}</span>
                                    {f.status === "open" && (
                                        <button onClick={() => resolve(f.id)} disabled={busy === f.id} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-2.5 py-1.5" data-testid={`resolve-${f.id}`}>
                                            <CheckCircle2 className="w-3.5 h-3.5" /> Resolve
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
